// CL-234 — Invio notifiche ai clienti del salone.
//
// Il client manda SOLO il filtro: i destinatari li risolve la RPC
// `get_broadcast_recipients` (SECURITY DEFINER, verifica che chi chiama sia il
// titolare del salone). La stessa RPC alimenta sia il conteggio mostrato nel
// modal sia l'invio vero e proprio nella edge function `send-shop-broadcast`,
// così il numero che l'utente legge è esattamente quello che parte.

import { supabase } from '@/lib/supabase';

export type BroadcastMode = 'all' | 'selected' | 'date';

export interface BroadcastFilter {
    mode: BroadcastMode;
    /** Solo per mode === 'selected' */
    customerIds?: string[];
    /** Solo per mode === 'date', formato yyyy-MM-dd */
    date?: string | null;
    /** Fascia oraria opzionale, formato HH:mm */
    from?: string | null;
    to?: string | null;
}

export interface BroadcastRecipient {
    customer_id: string;
    profile_id: string | null;
    full_name: string;
    phone: string | null;
    email: string | null;
    /** Ha un account app E almeno un token push registrato. */
    reachable: boolean;
}

export interface BroadcastResult {
    sent: number;
    devices: number;
    failed?: number;
    emails?: number;
    emails_failed?: number;
    unreachable: number;
    broadcast_id?: number | null;
    error?: string;
}

export const MAX_TITLE_LENGTH = 60;
export const MAX_BODY_LENGTH = 180;

/** Traduce il filtro della UI nei parametri della RPC. */
export const buildRecipientsParams = (shopId: string | number, filter: BroadcastFilter) => ({
    p_shop_id: Number(shopId),
    p_mode: filter.mode,
    p_customer_ids: filter.mode === 'selected' ? (filter.customerIds ?? []) : null,
    p_date: filter.mode === 'date' ? (filter.date ?? null) : null,
    p_from: filter.mode === 'date' && filter.from ? filter.from : null,
    p_to: filter.mode === 'date' && filter.to ? filter.to : null,
});

/** Il filtro è completo abbastanza da poter interrogare il server? */
export const isFilterReady = (filter: BroadcastFilter): boolean => {
    if (filter.mode === 'selected') return (filter.customerIds ?? []).length > 0;
    if (filter.mode === 'date') return Boolean(filter.date);
    return true;
};

export interface RecipientsSummary {
    total: number;
    /** Riceve la push: ha l'app e un token registrato. */
    reachable: number;
    /** Non ha l'app ma ha un'email: raggiungibile solo attivando il fallback. */
    emailable: number;
    /** Né app né email: non c'è modo di avvisarlo. */
    unreachable: number;
}

export const summarizeRecipients = (recipients: BroadcastRecipient[]): RecipientsSummary => {
    const reachable = recipients.filter((r) => r.reachable).length;
    const emailable = recipients.filter((r) => !r.reachable && Boolean(r.email)).length;
    return {
        total: recipients.length,
        reachable,
        emailable,
        unreachable: recipients.length - reachable - emailable,
    };
};

/** Quante persone riceveranno davvero il messaggio, dato lo stato del flag email. */
export const countTargets = (summary: RecipientsSummary, sendEmail: boolean): number =>
    summary.reachable + (sendEmail ? summary.emailable : 0);

export interface MessageValidation {
    isValid: boolean;
    message?: string;
}

export const validateBroadcastMessage = (title: string, body: string): MessageValidation => {
    if (!title.trim()) return { isValid: false, message: 'Il titolo è obbligatorio' };
    if (!body.trim()) return { isValid: false, message: 'Il messaggio è obbligatorio' };
    if (title.length > MAX_TITLE_LENGTH)
        return { isValid: false, message: `Il titolo non può superare ${MAX_TITLE_LENGTH} caratteri` };
    if (body.length > MAX_BODY_LENGTH)
        return { isValid: false, message: `Il messaggio non può superare ${MAX_BODY_LENGTH} caratteri` };
    return { isValid: true };
};

/**
 * Fascia oraria: entrambi gli estremi sono facoltativi.
 *   nessuno → tutta la giornata
 *   solo inizio → da quell'ora in poi
 *   solo fine → fino a quell'ora
 * L'unico caso invalido è un intervallo rovesciato.
 */
export const validateTimeRange = (from: string, to: string): MessageValidation => {
    if (from && to && from >= to)
        return { isValid: false, message: "L'orario di inizio deve precedere quello di fine" };
    return { isValid: true };
};

/** Descrizione leggibile della fascia scelta, per il riepilogo sotto i campi. */
export const describeTimeRange = (from: string, to: string): string => {
    if (from && to) return `Dalle ${from} alle ${to}`;
    if (from) return `Dalle ${from} in poi`;
    if (to) return `Fino alle ${to}`;
    return 'Tutta la giornata';
};

/** Lista destinatari per il filtro corrente (inclusi i non raggiungibili). */
export const fetchBroadcastRecipients = async (
    shopId: string | number,
    filter: BroadcastFilter,
): Promise<BroadcastRecipient[]> => {
    const { data, error } = await supabase.rpc(
        'get_broadcast_recipients',
        buildRecipientsParams(shopId, filter),
    );

    if (error) throw new Error(error.message);
    return (data ?? []) as BroadcastRecipient[];
};

/** Invia il messaggio. La edge function ri-risolve i destinatari dal filtro. */
export const sendBroadcast = async (
    shopId: string | number,
    filter: BroadcastFilter,
    title: string,
    body: string,
    sendEmail = false,
): Promise<BroadcastResult> => {
    const { data, error } = await supabase.functions.invoke('send-shop-broadcast', {
        body: {
            shop_id: Number(shopId),
            title: title.trim(),
            body: body.trim(),
            send_email: sendEmail,
            mode: filter.mode,
            customer_ids: filter.mode === 'selected' ? (filter.customerIds ?? []) : null,
            date: filter.mode === 'date' ? (filter.date ?? null) : null,
            from: filter.mode === 'date' && filter.from ? filter.from : null,
            to: filter.mode === 'date' && filter.to ? filter.to : null,
        },
    });

    if (error) throw new Error(error.message);
    if (data?.error && !data?.sent) throw new Error(data.error);

    return data as BroadcastResult;
};
